import {
  Box,
  Flex,
  HStack,
  Button,
  Container,
  IconButton,
  VStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Text,
} from '@chakra-ui/react';
import { NavLink } from '../common/NavLink';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FaLeaf } from 'react-icons/fa';
import { motion } from 'framer-motion';

const LeafIcon = ({ size = 20 }: { size?: number }): React.ReactElement =>
  React.createElement(FaLeaf as React.ComponentType<any>, { color: '#1a5e3f', size });

const Navbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const MenuItems = ({ isMobile = false }: { isMobile?: boolean }) => {
    const { isAuthenticated, user, logout } = useAuth();

    const navItemStyle = isMobile
      ? {
          py: 3,
          px: 4,
          borderRadius: '8px',
        }
      : {
          fontSize: '14px',
          fontWeight: '500',
          position: 'relative' as const,
        };

    return (
      <>
        <NavLink
          to="/"
          _hover={{
            color: 'primary.500',
            transition: 'color 0.2s ease',
          }}
          {...navItemStyle}
          color={isMobile ? 'neutral.700' : 'neutral.600'}
        >
          Home
        </NavLink>
        
        <NavLink
          to="/about"
          _hover={{
            color: 'primary.500',
            transition: 'color 0.2s ease',
          }}
          {...navItemStyle}
          color={isMobile ? 'neutral.700' : 'neutral.600'}
        >
          About
        </NavLink>

        {isAuthenticated && user?.role && ['farmer', 'officer'].includes(user.role) && (
          <NavLink
            to={user.role === 'farmer' ? '/dashboard' : '/officer-dashboard'}
            _hover={{
              color: 'primary.500',
              transition: 'color 0.2s ease',
            }}
            {...navItemStyle}
            color={isMobile ? 'neutral.700' : 'neutral.600'}
          >
            Dashboard
          </NavLink>
        )}
        
        {isAuthenticated && user?.role && ['farmer', 'buyer'].includes(user.role) && (
          <NavLink
            to="/marketplace"
            _hover={{
              color: 'primary.500',
              transition: 'color 0.2s ease',
            }}
            {...navItemStyle}
            color={isMobile ? 'neutral.700' : 'neutral.600'}
          >
            Marketplace
          </NavLink>
        )}
        
        {isAuthenticated && (
          <NavLink
            to="/profile"
            _hover={{
              color: 'primary.500',
              transition: 'color 0.2s ease',
            }}
            {...navItemStyle}
            color={isMobile ? 'neutral.700' : 'neutral.600'}
          >
            Profile
          </NavLink>
        )}
        
        <NavLink
          to="/chatbot"
          _hover={{
            color: 'primary.500',
            transition: 'color 0.2s ease',
          }}
          {...navItemStyle}
          color={isMobile ? 'neutral.700' : 'neutral.600'}
        >
          Support
        </NavLink>
        
        <NavLink
          to="/ussd"
          _hover={{
            color: 'primary.500',
            transition: 'color 0.2s ease',
          }}
          {...navItemStyle}
          color={isMobile ? 'neutral.700' : 'neutral.600'}
        >
          USSD
        </NavLink>

        {!isAuthenticated ? (
          <>
            <NavLink to="/login">
              <Button
                variant="ghost"
                size="sm"
                fontSize="14px"
                fontWeight="600"
                color="primary.500"
                _hover={{ bg: 'primary.50' }}
              >
                Login
              </Button>
            </NavLink>
            <NavLink to="/register">
              <Button
                variant="primary"
                size="sm"
                fontSize="14px"
                fontWeight="600"
              >
                Register
              </Button>
            </NavLink>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            fontSize="14px"
            fontWeight="600"
            color="neutral.700"
            _hover={{ bg: 'neutral.100' }}
            onClick={logout}
          >
            Logout
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <motion.div
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={100}
          bg="linear-gradient(to bottom, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))"
          backdropFilter="blur(10px)"
          borderBottom="1px solid"
          borderColor="neutral.200"
          boxShadow="0 2px 12px rgba(0, 0, 0, 0.06)"
          py={4}
        >
          <Container maxW="container.xl">
            <Flex justify="space-between" align="center">
              {/* Brand Logo */}
              <NavLink to="/">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <HStack spacing={2} cursor="pointer">
                    <Box
                      p={2}
                      bg="linear-gradient(135deg, rgba(26, 94, 63, 0.08), rgba(26, 94, 63, 0.04))"
                      borderRadius="8px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="1px solid"
                      borderColor="primary.200"
                    >
                      <LeafIcon size={20} />
                    </Box>
                    <VStack spacing={0} align="flex-start">
                      <Text
                        fontSize="16px"
                        fontWeight="700"
                        color="neutral.900"
                        letterSpacing="-0.5px"
                      >
                        MarketLink
                      </Text>
                      <Text
                        fontSize="10px"
                        fontWeight="500"
                        color="primary.500"
                        letterSpacing="1px"
                        textTransform="uppercase"
                      >
                        Zimbabwe
                      </Text>
                    </VStack>
                  </HStack>
                </motion.div>
              </NavLink>

              {/* Desktop Navigation */}
              <HStack spacing={8} display={{ base: 'none', lg: 'flex' }}>
                <MenuItems />
              </HStack>

              {/* Mobile Hamburger Menu */}
              <IconButton
                display={{ base: 'flex', lg: 'none' }}
                aria-label="Open menu"
                icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                onClick={isOpen ? onClose : onOpen}
                variant="ghost"
                size="lg"
                color="neutral.700"
                _hover={{ bg: 'neutral.100' }}
              />
            </Flex>
          </Container>
        </Box>
      </motion.div>

      {/* Add padding to account for fixed navbar */}
      <Box h="64px" />

      {/* Mobile Drawer Navigation */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="right">
        <DrawerOverlay backdropFilter="blur(2px)" />
        <DrawerContent bg="white" borderLeft="1px solid" borderLeftColor="neutral.200">
          <DrawerHeader
            borderBottomWidth="1px"
            borderColor="neutral.200"
            py={4}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <HStack spacing={2}>
              <Box
                p={2}
                bg="primary.50"
                borderRadius="8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <LeafIcon size={18} />
              </Box>
              <Text fontSize="16px" fontWeight="700" color="neutral.900">
                Menu
              </Text>
            </HStack>
            <DrawerCloseButton position="relative" />
          </DrawerHeader>
          <DrawerBody pt={4}>
            <VStack spacing={2} align="stretch">
              <MenuItems isMobile={true} />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Navbar;